/**
 * Portail d'accès pré-lancement de l'eshop.
 * Masque toutes les pages /shop derrière un code partagé tant que le backend
 * (ESHOP_PRELAUNCH_CODE) l'exige. Une fois le bon code saisi, l'accès est
 * mémorisé sur l'appareil. Au lancement, vider la variable serveur → le portail
 * se désactive tout seul. N'affecte QUE l'eshop (pas le POS / dashboard).
 */
import { useEffect, useState, FormEvent } from 'react';
import { Outlet } from 'react-router-dom';

const KEY = 'shop_access_granted';

export default function ShopGate() {
  const [state, setState] = useState<'loading' | 'open' | 'locked'>('loading');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(KEY) === '1') {
      setState('open');
      return;
    }
    fetch('/api/shop-access')
      .then((r) => r.json())
      .then((d) => {
        if (!d?.required) {
          localStorage.setItem(KEY, '1');
          setState('open');
        } else {
          setState('locked');
        }
      })
      // Réseau absent : on ne sait pas si un code est exigé — et le portail est
      // retiré depuis le lancement (26/08/2026). Verrouiller montrait un écran
      // « Boutique en préparation » à tout visiteur ouvrant la boutique hors
      // ligne ; on laisse passer, la boutique dit elle-même « Pas de connexion ».
      .catch(() => setState('open'));
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const r = await fetch('/api/shop-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() }),
      });
      if (!r.ok) throw new Error('bad code');
      localStorage.setItem(KEY, '1');
      setState('open');
    } catch {
      setError('Code d’accès incorrect. Réessayez.');
    } finally {
      setSubmitting(false);
    }
  };

  if (state === 'open') return <Outlet />;

  if (state === 'loading') {
    return <div className="min-h-screen bg-[#f7f5f0]" />;
  }

  // Portail verrouillé
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f7f5f0] px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
        <img
          src="/logo-folies.png"
          alt="Les Folies Maraîchères"
          className="h-20 w-auto mx-auto mb-6"
        />
        <h1 className="text-xl font-bold text-[#2d6a4f]">Boutique en préparation</h1>
        <p className="mt-2 text-sm text-gray-600">
          Notre boutique en ligne ouvre bientôt. Entrez votre code d’accès pour la découvrir en avant-première.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-3">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Code d’accès"
            autoFocus
            autoCapitalize="characters"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center tracking-wide focus:outline-none focus:ring-2 focus:ring-[#2d6a4f] focus:border-[#2d6a4f]"
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button
            type="submit"
            disabled={submitting || !code.trim()}
            className="w-full py-3 bg-[#2d6a4f] hover:bg-[#245840] disabled:opacity-50 text-white font-medium rounded-lg transition-colors"
          >
            {submitting ? 'Vérification…' : 'Entrer'}
          </button>
        </form>
      </div>
    </div>
  );
}
